import React, { Component } from "react";
import cn from "classnames";
import { FormattedMessage } from "react-intl";

import { aspectRatio as ar } from "../../../../../common/parameters";

import player from "../../../../player";
import Icon from "../../../../icon";

import Score from "../../../Score/score-display";

import Wrapper from "../../Wrapper";

import getBirdsPath from "../Common/getBirdsPath";
import MessageDisplay from "../Common/MessageDisplay";
import NotEdited from "../Common/NotEdited";
import getScale from "../Common/getScale";
import getScore from "../Common/getScore";
import getTempo from "../Common/getTempo";
import scrollToBar from "../Common/scrollToBar";

import { SubmitButton, PlayBtn, Label } from "../common-style";

import RhytRecorder from "./RhytRecorder";
import { ScoreWrapper, Asset1Wrapper, Asset2Wrapper, SoundButton, EasterBackground, ChristmasBackground } from "./styles";
import messages from "./messages";
import store from "./store";
import { getIncorrectNotes } from "./utils";
import { IProps, IState } from "./interfaces";
import { ICurrentNote, IScore } from "../../../../../common/types";

const Clock = require("waaclock");


class RhytKnock extends Component<IProps, IState> {
    buffers: AudioBufferSourceNode[] = [];
    clock: any;
    distribution?: number[][];
    scoreRef?: HTMLDivElement;
    static defaultProps = {
        handlePlayCount: () => {},
        handleFailCount: () => {},
    };
    constructor(props: IProps) {
        super(props);
        const { plan, task } = props;
        let score: IScore | undefined = undefined;
        if (getScore(plan, task)) {
            score = JSON.parse(JSON.stringify(getScore(plan, task)));
            for (let a = 0; a < 5; a++) {
                delete score!["bars" + (a + 1)];
            }
            score!.bars.forEach(bar => {
                if (!bar) return;
                bar.notes.forEach(note => {
                    delete note.p;
                });
            });
            score!.settings.upperText = 0;
            if (!task.data.score) {
                score!.settings.staffLines = false;
                score!.settings.stick = false;
            }
        }
        this.state = {
            playing: false,
            completed: false,
            incorrectNotes: [[]],
            failed: false,
            score,
            playable: false,
            firstBar: 1,
            playingNote: undefined,
            currentNote: null,
        };
        if (props.setReset) {
            props.setReset(this.resetTask);
        }
        if (props.saveState) {
            props.saveState(this.saveState);
        }
        if (props.restoreState) {
            props.restoreState(this.restoreState);
        }
        if (props.showSolution) {
            props.showSolution(this.showSolution);
        }
    }
    resetTask = () => {
        this.setState({
            completed: false,
        });
    };
    saveState = () => {
        return {
            completed: this.state.completed,
        };
    };
    restoreState = (state: IState) => {
        this.setState({
            completed: state.completed,
        });
    };
    showSolution = () => {
        this.setState({
            completed: true,
        });
    };
    componentDidMount() {
        this.setUp();
    }

    componentWillUnmount() {
        window.removeEventListener("blur", this.blur);
        if (this.state.playing) {
            this.play();
        }
    }

    setUp = () => {
        const { task, plan } = this.props;
        const score = getScore(plan, task);
        if (score && score.bars[1] && score.bars[1].notes && score.bars[1].notes.length > 0) {
            this.setState({ playable: true, firstBar: score.bars[0] ? 0 : 1 });
        }
    };

    blur = () => {
        this.play();
    };

    play = async () => {
        const { playing } = this.state;
        if (playing) {
            this.buffers.forEach(buffer => {
                buffer.stop();
            });
            this.buffers = [];
            window.removeEventListener("blur", this.blur);
            this.setState({ playing: false, playingNote: undefined });
            if (this.clock) {
                this.clock.stop();
                delete this.clock;
            }
            return;
        }
        if (!player.slap) {
            const success = await player.loadSlap();
            if (!success) return this.props.showNotification("Error: Failed to load audio!", "warning");
        }
        window.addEventListener("blur", this.blur);
        let t = 0;
        let late = 0.2;
        this.clock = new Clock(player.context, { toleranceEarly: 0.1 });
        this.clock.start();
        const { task, plan } = this.props;
        this.props.handlePlayCount!();
        let tempo = getTempo(plan, task);
        let lastNote;
        this.buffers = [];
        const score = getScore(plan, task)!;

        const offset = player.context.currentTime + late;
        for (let b = 0; b < score.bars.length; b++) {
            let bar = score.bars[b];
            if (!bar) continue;
            for (let n = 0; n < bar.notes.length; n++) {
                let note = bar.notes[n];
                if (!note.r && (!lastNote || !lastNote.tie)) {
                    let volume = n === 0 ? "forte" : "mezzo";
                    let bufferNode = player.slap!.createNode(volume);
                    bufferNode.start(offset + t);
                    this.buffers.push(bufferNode);
                }
                this.clock.callbackAtTime(() => {
                    this.setState({ playingNote: this.state.playingNote! + 1 });
                    let isScrolling = n === 0;
                    if (isScrolling && this.scoreRef) {
                        scrollToBar(this.distribution!, this.scoreRef, b);
                    }
                }, offset + t);
                lastNote = note;
                t += (4 / note.l) * (60 / tempo) * (note.d ? 1.5 : 1) * (note.t ? 0.67 : 1);
            }
        }
        this.clock.callbackAtTime(this.play, offset + t);

        this.setState({ playing: true, playingNote: -1 });
    };

    setCurrentNote = (currentNote: ICurrentNote | null) => {
        if (currentNote) {
            this.setState({
                currentNote,
            });
        } else {
            this.setState({
                currentNote: null,
            });
        }
        if (currentNote) {
            setTimeout(() => {
                scrollToBar(this.distribution!, this.scoreRef!, currentNote.bar);
            }, 500);
        }
    };

    completed = () => {
        this.setState({ completed: true });
        this.props.completed(this.props.task.type);
    };

    failed = (issue: string, incorrectNotes?: { bar: number, note: number }[]) => {
        this.props.handleFailCount!();
        switch (issue) {
            case "timeup":
            case "slow":
            case "fast":
                this.setState({ failed: true, issue, currentNote: null });
                break;
            default:
                const { task, plan } = this.props;
                const score = getScore(plan, task)!;
                this.setState({
                    failed: true,
                    issue: "notes",
                    currentNote: null,
                    incorrectNotes: getIncorrectNotes(score, incorrectNotes!),
                });
                break;
        }
    };

    tryAgain = () => {
        this.setState({ failed: false, incorrectNotes: [[]] });
    };

    setRef = (ref: HTMLDivElement) => {
        this.scoreRef = ref;
    };

    setDistribution = (distribution: number[][]) => {
        this.distribution = distribution;
    };

    render() {
        const {
            incorrectNotes,
            playable,
            playing,
            completed,
            playingNote,
            currentNote,
            failed,
            issue,
            score,
            firstBar,
        } = this.state;
        const { task, isLow, isDemo, plan, preview, handleHomeworkNext, isEaster, isChristmas } = this.props;
        const { data } = task;
        if (!data || !playable)
            return (
                <div>
                    <div>
                        <Wrapper>
                            <NotEdited />
                        </Wrapper>
                    </div>
                </div>
            );
        const scale = getScale(plan, task);
        const tempo = getTempo(plan, task);
        return (
            <div>
                <Wrapper isDemo={isDemo}>
                    {isLow && (
                        <>
                            <Asset1Wrapper>
                                <img src={getBirdsPath("/rhytKnock/asset-01.svg")} alt="decoration" />
                            </Asset1Wrapper>
                            <Asset2Wrapper>
                                <img src={getBirdsPath("/rhytKnock/asset-02.svg")} alt="decoration" />
                            </Asset2Wrapper>
                        </>
                    )}
                    {isEaster && (
                        <EasterBackground />
                    )}
                    {isChristmas && (
                        <ChristmasBackground />
                    )}
                    <ScoreWrapper isLow={isLow} isEaster={isEaster || isChristmas}>
                        <Score
                            score={score!}
                            rhytName
                            singing
                            activeNote={currentNote || undefined}
                            activeVoice={0}
                            currentNote={currentNote || undefined}
                            playingNote={playingNote}
                            scale={scale * (isLow ? 0.5425 : (isEaster || isChristmas) ? (1076 / ar.width) : 1)}
                            incorrectNotes={incorrectNotes}
                            whiteBackground={isLow}
                            playing={playing}
                            setRef={this.setRef}
                            setDistribution={this.setDistribution}
                            isEaster={isEaster || isChristmas}
                        />
                    </ScoreWrapper>

                    <PlayBtn isEaster={isEaster} className={cn("button", "button--play")} onClick={() => this.play()} isLow={isLow}>
                        {playing ? <Icon i="stop" /> : <Icon i="play" />}
                        <Label>
                            <FormattedMessage {...messages.play} />
                        </Label>
                    </PlayBtn>
                    {!completed && !failed && (
                        <RhytRecorder
                            isEaster={!!isEaster}
                            score={score!}
                            isLow={!!isLow}
                            accuracy={data.accuracy || 2}
                            tempo={tempo}
                            setActiveNote={this.setCurrentNote}
                            completed={this.completed}
                            mistake={this.failed}
                            preview={!!preview}
                            playing={playing}
                            firstBar={firstBar}
                        />
                    )}
                    {failed && (
                        <SubmitButton
                            isEaster={isEaster}
                            isLow={isLow}
                            style={{ bottom: isLow ? 185 : isEaster ? 42 : 3 }}
                            className={cn("button")}
                            onClick={() => {
                                this.tryAgain();
                            }}
                        >
                            <FormattedMessage {...messages.tryAgain} />
                        </SubmitButton>
                    )}
                    {!completed && (
                        <SoundButton isActive={store.get()} isLow={isLow} onClick={() => store.toggle.call(this)}>
                            {store.get() ? <Icon i="sound" /> : <Icon i="soundOff" />}
                        </SoundButton>
                    )}
                    {completed && (
                        <MessageDisplay isChristmas={isChristmas} isLow={!!isLow} type="completed" handleHomeworkNext={handleHomeworkNext} />
                    )}
                    {failed && issue === "notes" && <MessageDisplay isChristmas={isChristmas} isLow={!!isLow} type="incorrect" />}
                    {failed && issue === "timeup" && <MessageDisplay isChristmas={isChristmas} isLow={!!isLow} type="timeup" />}
                    {failed && issue === "slow" && <MessageDisplay isChristmas={isChristmas} isLow={!!isLow} type="slow" />}
                    {failed && issue === "fast" && <MessageDisplay isChristmas={isChristmas} isLow={!!isLow} type="fast" />}
                </Wrapper>
            </div>
        );
    }
}

export default RhytKnock;
